import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, FlaskConical, Loader2, Pencil, Play, Plus, Trash2,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { AddVideoDialog } from '@/components/admin/community-curation/AddVideoDialog';
import { NicheFormDialog } from '@/components/admin/community-curation/NicheFormDialog';
import { RunPicksDialog } from '@/components/admin/community-curation/RunPicksDialog';
import { RunErrorText, RunStatusBadge } from '@/components/admin/community-curation/RunStatus';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { isBudgetBelowEstimate } from '@/lib/community-curation/isBudgetBelowEstimate';
import { formatDateTime } from '@/lib/format/datetime';
import { isRunSettled } from '@/lib/community-curation/isRunSettled';
import { mapCurationError } from '@/lib/community-curation/mapCurationError';
import { reasonLabelKey } from '@/lib/community-curation/reasonLabelKey';
import type {
  CurationNiche, CurationNicheInput, CurationPick, CurationRegion, CurationRun, CurationSettings,
} from '@/lib/community-curation/types';
import {
  ADMIN_ADD_COMMUNITY_CURATION_VIDEO,
  ADMIN_COMMUNITY_CURATION_OVERVIEW,
  ADMIN_COMMUNITY_CURATION_RUN,
  ADMIN_COMMUNITY_CURATION_RUNS,
  ADMIN_CREATE_COMMUNITY_CURATION_NICHE,
  ADMIN_DELETE_COMMUNITY_CURATION_NICHE,
  ADMIN_RUN_COMMUNITY_CURATION,
  ADMIN_UPDATE_COMMUNITY_CURATION_NICHE,
  ADMIN_UPDATE_COMMUNITY_CURATION_SETTINGS,
} from '@/queries/communityCuration';

const RUNS_PAGE = 20;
const POLL_MS = 3000;
const RUNS_POLL_MS = 10000;

const toInt = (value: string): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
};

export default function AdminCommunityCuration() {
  const { t, i18n } = useTranslation();
  const formatUnits = (value: number) => value.toLocaleString(i18n.language);
  const { toast } = useToast();

  const [tab, setTab] = useState('niches');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [nicheDialog, setNicheDialog] = useState<{ open: boolean; niche: CurationNiche | null }>({
    open: false, niche: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<CurationNiche | null>(null);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [viewRunId, setViewRunId] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [capDraft, setCapDraft] = useState('');
  const [budgetDraft, setBudgetDraft] = useState('');

  const overview = useQuery(ADMIN_COMMUNITY_CURATION_OVERVIEW);
  const runsQuery = useQuery(ADMIN_COMMUNITY_CURATION_RUNS, {
    variables: { limit: RUNS_PAGE, offset: 0 },
  });
  const activeRunQuery = useQuery(ADMIN_COMMUNITY_CURATION_RUN, {
    variables: { id: activeRunId },
    skip: !activeRunId,
  });

  const [updateSettings, updateSettingsState] = useMutation(ADMIN_UPDATE_COMMUNITY_CURATION_SETTINGS);
  const [createNiche, createNicheState] = useMutation(ADMIN_CREATE_COMMUNITY_CURATION_NICHE);
  const [updateNiche, updateNicheState] = useMutation(ADMIN_UPDATE_COMMUNITY_CURATION_NICHE);
  const [deleteNiche, deleteNicheState] = useMutation(ADMIN_DELETE_COMMUNITY_CURATION_NICHE);
  const [runCuration, runCurationState] = useMutation(ADMIN_RUN_COMMUNITY_CURATION);
  const [addVideo, addVideoState] = useMutation(ADMIN_ADD_COMMUNITY_CURATION_VIDEO);

  const settings: CurationSettings | undefined = overview.data?.adminCommunityCurationSettings;
  const niches: CurationNiche[] = overview.data?.adminCommunityCurationNiches ?? [];
  const runs: CurationRun[] = runsQuery.data?.adminCommunityCurationRuns?.data ?? [];
  const activeRun: CurationRun | null = activeRunQuery.data?.adminCommunityCurationRun ?? null;

  const estimatedUnits = settings?.estimated_units_per_run ?? 0;
  const budgetUnits = settings?.youtube_quota_budget ?? 0;
  const budgetHeld = !!settings && isBudgetBelowEstimate(budgetUnits, estimatedUnits);
  const draftBudget = toInt(budgetDraft);
  const draftBelowEstimate = draftBudget !== undefined
    && draftBudget !== budgetUnits
    && isBudgetBelowEstimate(draftBudget, estimatedUnits);

  const showError = useCallback((error: unknown) => {
    const { key, field } = mapCurationError(error);
    setErrorMessage(t(key, {
      field: field ? t(`admin.communityCuration.fields.${field}`, { defaultValue: field }) : '',
      budget: formatUnits(budgetUnits),
      estimated: formatUnits(estimatedUnits),
    }));
  }, [t, budgetUnits, estimatedUnits]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = useCallback(() => {
    overview.refetch();
    runsQuery.refetch();
  }, [overview, runsQuery]);

  useEffect(() => {
    if (!settings) return;
    setCapDraft(String(settings.global_daily_cap));
    setBudgetDraft(String(settings.youtube_quota_budget));
  }, [settings?.global_daily_cap, settings?.youtube_quota_budget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the run this page started until it settles, then report and refresh.
  const { startPolling, stopPolling } = activeRunQuery;
  const activeSettled = !!activeRun && activeRun.id === activeRunId && isRunSettled(activeRun);
  useEffect(() => {
    if (!activeRunId || activeSettled) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_MS);
    return () => stopPolling();
  }, [activeRunId, activeSettled, startPolling, stopPolling]);

  useEffect(() => {
    if (!activeSettled || !activeRun) return;
    setActiveRunId(null);
    refreshAll();
    if (activeRun.status === 'failed') {
      toast({ variant: 'destructive', description: t('admin.communityCuration.runFailed') });
    } else if (activeRun.dry_run) {
      toast({ description: t('admin.communityCuration.simulationFinished', { added: activeRun.added_count }) });
    } else {
      toast({
        description: t('admin.communityCuration.runFinished', {
          added: activeRun.added_count,
          skipped: activeRun.skipped_count,
          failed: activeRun.failed_count,
        }),
      });
    }
  }, [activeSettled]); // eslint-disable-line react-hooks/exhaustive-deps

  // A run started elsewhere (the daily cron, Claude) shows up as running in
  // the list; keep the list fresh until it finishes.
  const anyRunning = runs.some((run) => run.status === 'running');
  const { startPolling: startRunsPolling, stopPolling: stopRunsPolling } = runsQuery;
  useEffect(() => {
    if (anyRunning) startRunsPolling(RUNS_POLL_MS);
    else stopRunsPolling();
    return () => stopRunsPolling();
  }, [anyRunning, startRunsPolling, stopRunsPolling]);

  const saveSettings = async (input: Record<string, unknown>) => {
    try {
      await updateSettings({ variables: { input } });
      toast({ description: t('admin.communityCuration.saved') });
      overview.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const startRun = async (dryRun: boolean) => {
    try {
      const { data } = await runCuration({ variables: { dryRun } });
      const run: CurationRun | undefined = data?.adminRunCommunityCuration;
      if (run) setActiveRunId(run.id);
      toast({
        description: dryRun
          ? t('admin.communityCuration.simulationStarted')
          : t('admin.communityCuration.runStarted'),
      });
      setTab('runs');
      runsQuery.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const saveNiche = async (input: CurationNicheInput) => {
    try {
      if (nicheDialog.niche) {
        await updateNiche({ variables: { id: nicheDialog.niche.id, input } });
      } else {
        await createNiche({ variables: { input } });
      }
      setNicheDialog({ open: false, niche: null });
      toast({ description: t('admin.communityCuration.niches.saved') });
      overview.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const toggleNiche = async (niche: CurationNiche, enabled: boolean) => {
    try {
      await updateNiche({ variables: { id: niche.id, input: { enabled } } });
      overview.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteNiche({ variables: { id: deleteTarget.id } });
      setDeleteTarget(null);
      toast({ description: t('admin.communityCuration.niches.deleted') });
      overview.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const submitVideo = async (input: { url: string; nicheId: string | null; region: CurationRegion }) => {
    try {
      const { data } = await addVideo({ variables: input });
      const pick: CurationPick | undefined = data?.adminAddCommunityCurationVideo;
      setAddVideoOpen(false);
      if (pick?.status === 'added') {
        toast({ description: t('admin.communityCuration.addVideoDialog.added') });
      } else if (pick?.status === 'skipped') {
        toast({
          description: t('admin.communityCuration.addVideoDialog.skipped', {
            reason: t(reasonLabelKey(pick.reason)),
          }),
        });
      } else {
        toast({ variant: 'destructive', description: t('admin.communityCuration.addVideoDialog.failed') });
      }
      refreshAll();
    } catch (error) {
      showError(error);
    }
  };

  const running = !!activeRunId || runCurationState.loading;
  const nicheSaving = createNicheState.loading || updateNicheState.loading;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.communityCuration.title')}</CardTitle>
            <CardDescription>{t('admin.communityCuration.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {overview.error && (
              <div className="space-y-2 text-center">
                <p className="text-destructive">{t('admin.communityCuration.loadError')}</p>
                <Button size="sm" variant="secondary" onClick={() => { overview.refetch(); }}>
                  {t('admin.communityCuration.retry')}
                </Button>
              </div>
            )}

            {settings && (
              <>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="curation-enabled"
                        data-testid="curation-enabled"
                        checked={settings.enabled}
                        disabled={updateSettingsState.loading}
                        onCheckedChange={(enabled) => saveSettings({ enabled })}
                      />
                      <Label htmlFor="curation-enabled">{t('admin.communityCuration.enabled')}</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('admin.communityCuration.enabledHint')}</p>
                  </div>
                  <div className="space-y-1 text-right text-sm">
                    <p>
                      <span className="text-muted-foreground">{t('admin.communityCuration.curator')}: </span>
                      {settings.curator?.name || (
                        <span className="text-destructive">{t('admin.communityCuration.curatorMissing')}</span>
                      )}
                    </p>
                    <p data-testid="curation-added-today">
                      {t('admin.communityCuration.addedToday', {
                        count: settings.added_today, cap: settings.global_daily_cap,
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="curation-cap">{t('admin.communityCuration.globalDailyCap')}</Label>
                    <Input
                      id="curation-cap"
                      type="number"
                      className="w-32"
                      value={capDraft}
                      onChange={(e) => setCapDraft(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="curation-budget">{t('admin.communityCuration.youtubeQuotaBudget')}</Label>
                    <Input
                      id="curation-budget"
                      type="number"
                      className="w-40"
                      value={budgetDraft}
                      onChange={(e) => setBudgetDraft(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground" data-testid="curation-estimate">
                      {t('admin.communityCuration.estimatePerRun', { estimated: formatUnits(estimatedUnits) })}
                    </p>
                    {draftBelowEstimate && (
                      <p className="text-xs text-amber-500">
                        {t('admin.communityCuration.budgetDraftBelow', { estimated: formatUnits(estimatedUnits) })}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    disabled={updateSettingsState.loading}
                    onClick={() => saveSettings({
                      globalDailyCap: toInt(capDraft),
                      youtubeQuotaBudget: toInt(budgetDraft),
                    })}
                  >
                    {t('admin.communityCuration.save')}
                  </Button>
                </div>

                {budgetHeld && (
                  <Alert className="border-amber-500/60 text-amber-500" data-testid="curation-budget-warning">
                    <AlertTriangle className="h-4 w-4 !text-amber-500" />
                    <AlertTitle>{t('admin.communityCuration.budgetWarningTitle')}</AlertTitle>
                    <AlertDescription>
                      {t('admin.communityCuration.budgetWarning', {
                        budget: formatUnits(budgetUnits),
                        estimated: formatUnits(estimatedUnits),
                      })}
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="curation-run-now" disabled={running} onClick={() => startRun(false)}>
                <Play className="mr-2 h-4 w-4" />
                {t('admin.communityCuration.runNow')}
              </Button>
              <Button
                data-testid="curation-simulate"
                variant="secondary"
                disabled={running}
                onClick={() => startRun(true)}
              >
                <FlaskConical className="mr-2 h-4 w-4" />
                {t('admin.communityCuration.simulate')}
              </Button>
              <Button data-testid="curation-add-video" variant="outline" onClick={() => setAddVideoOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('admin.communityCuration.addVideo')}
              </Button>
              {running && (
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('admin.communityCuration.running')}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="niches">{t('admin.communityCuration.tabs.niches')}</TabsTrigger>
            <TabsTrigger value="runs">{t('admin.communityCuration.tabs.runs')}</TabsTrigger>
          </TabsList>

          <TabsContent value="niches">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">{t('admin.communityCuration.tabs.niches')}</CardTitle>
                <Button
                  size="sm"
                  data-testid="curation-new-niche"
                  onClick={() => setNicheDialog({ open: true, niche: null })}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('admin.communityCuration.niches.new')}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.communityCuration.niches.columns.name')}</TableHead>
                      <TableHead>{t('admin.communityCuration.niches.columns.region')}</TableHead>
                      <TableHead>{t('admin.communityCuration.niches.columns.sources')}</TableHead>
                      <TableHead>{t('admin.communityCuration.niches.columns.dailyLimit')}</TableHead>
                      <TableHead>{t('admin.communityCuration.niches.columns.enabled')}</TableHead>
                      <TableHead className="text-right">{t('admin.communityCuration.niches.columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!overview.error && !overview.loading && niches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          {t('admin.communityCuration.niches.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                    {niches.map((niche) => (
                      <TableRow key={niche.id} data-testid="curation-niche-row">
                        <TableCell className="font-medium">{niche.name}</TableCell>
                        <TableCell>{t(`admin.communityCuration.regions.${niche.region}`)}</TableCell>
                        <TableCell className="max-w-[320px] text-sm">
                          <p className="truncate">{niche.keywords.join(', ') || '—'}</p>
                          {niche.seed_channels.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {t('admin.communityCuration.niches.channelsCount', { count: niche.seed_channels.length })}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{niche.daily_limit}</TableCell>
                        <TableCell>
                          <Switch
                            checked={niche.enabled}
                            aria-label={t('admin.communityCuration.niches.columns.enabled')}
                            onCheckedChange={(enabled) => toggleNiche(niche, enabled)}
                          />
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={t('admin.communityCuration.niches.edit')}
                            onClick={() => setNicheDialog({ open: true, niche })}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={t('admin.communityCuration.niches.delete')}
                            onClick={() => setDeleteTarget(niche)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="runs">
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.communityCuration.runs.columns.startedAt')}</TableHead>
                      <TableHead>{t('admin.communityCuration.runs.columns.trigger')}</TableHead>
                      <TableHead>{t('admin.communityCuration.runs.columns.status')}</TableHead>
                      <TableHead>{t('admin.communityCuration.runs.columns.result')}</TableHead>
                      <TableHead className="text-right">{t('admin.communityCuration.runs.columns.units')}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runsQuery.error && (
                      <TableRow>
                        <TableCell colSpan={6} className="space-y-2 text-center">
                          <p className="text-destructive">{t('admin.communityCuration.runs.loadError')}</p>
                          <Button size="sm" variant="secondary" onClick={() => { runsQuery.refetch(); }}>
                            {t('admin.communityCuration.retry')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                    {!runsQuery.error && !runsQuery.loading && runs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          {t('admin.communityCuration.runs.empty')}
                        </TableCell>
                      </TableRow>
                    )}
                    {runs.map((run) => (
                      <TableRow key={run.id} data-testid="curation-run-row">
                        <TableCell className="text-sm">{formatDateTime(run.started_at)}</TableCell>
                        <TableCell className="space-x-1">
                          <Badge variant="outline">{t(`admin.communityCuration.triggers.${run.trigger}`)}</Badge>
                          {run.dry_run && (
                            <Badge variant="secondary">{t('admin.communityCuration.runs.dryRun')}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <RunStatusBadge status={run.status} />
                          <RunErrorText run={run} className="max-w-[220px] truncate" />
                        </TableCell>
                        <TableCell className="text-sm">
                          {t(run.dry_run ? 'admin.communityCuration.runs.resultDryRun' : 'admin.communityCuration.runs.result', {
                            added: run.added_count, skipped: run.skipped_count, failed: run.failed_count,
                          })}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{run.youtube_units_used}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid="curation-view-run"
                            onClick={() => setViewRunId(run.id)}
                          >
                            {t('admin.communityCuration.runs.view')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <NicheFormDialog
        open={nicheDialog.open}
        niche={nicheDialog.niche}
        saving={nicheSaving}
        onOpenChange={(open) => { if (!open) setNicheDialog({ open: false, niche: null }); }}
        onSubmit={saveNiche}
      />

      <AddVideoDialog
        open={addVideoOpen}
        niches={niches}
        saving={addVideoState.loading}
        onOpenChange={setAddVideoOpen}
        onSubmit={submitVideo}
      />

      <RunPicksDialog runId={viewRunId} onOpenChange={(open) => { if (!open) setViewRunId(null); }} />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.communityCuration.niches.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.communityCuration.niches.deleteDescription', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              {t('admin.communityCuration.form.cancel')}
            </Button>
            <Button variant="destructive" disabled={deleteNicheState.loading} onClick={confirmDelete}>
              {t('admin.communityCuration.niches.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.communityCuration.errors.title')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorMessage(null)}>{t('admin.communityCuration.errors.ok')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
